import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Paperclip, Loader2 } from "lucide-react";
import { API, http } from "../../lib/api";

const SESSION_KEY = "pluk_wingman_session";

const getSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = (window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9-]/g, "");
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
};

const Bubble = ({ m }) => (
  <div className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
    <p className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.from === "user" ? "rounded-br-sm bg-emerald-500 text-[#0a0c0e]" : "rounded-bl-sm bg-white/[0.06] text-zinc-100"}`}>
      {m.text}
    </p>
  </div>
);

export const Wingman = ({ openSignal = 0 }) => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const options = msgs.length && msgs[msgs.length - 1].from === "bot" ? msgs[msgs.length - 1].options ?? [] : [];

  const push = (replies) => {
    const list = Array.isArray(replies) ? replies : [];
    setMsgs((p) => [...p, ...list.map((r) => ({ from: "bot", text: r?.text ?? "", options: r?.options ?? [] }))]);
  };

  const send = async (t, silent = false) => {
    const value = (t ?? text).trim();
    if (!value || busy) return;
    if (!silent) setMsgs((p) => [...p, { from: "user", text: value }]);
    setText("");
    setBusy(true);
    try {
      const { data } = await http.post(`${API}/wingman/message`, { session_id: getSession(), text: value });
      push(data?.replies);
    } catch {
      push([{ text: "Sorry, I couldn't reach our team just now. Please try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setMsgs((p) => [...p, { from: "user", text: `Attached: ${file.name}` }]);
    const fd = new FormData();
    fd.append("session_id", getSession());
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await http.post(`${API}/wingman/upload`, fd);
      push(data?.replies);
    } catch {
      push([{ text: "That upload didn't go through. Please try again with a PDF or Word file under 5 MB." }]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (openSignal > 0) setOpen(true);
  }, [openSignal]);

  useEffect(() => {
    if (open && msgs.length === 0) send("restart", true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [msgs, busy]);

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} className="fixed bottom-5 right-5 z-[70] inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-3.5 text-sm font-medium text-[#0a0c0e] shadow-[0_10px_40px_-10px_rgba(201,161,59,0.7)] hover:bg-emerald-400 transition-colors" data-testid="wingman-launcher">
        {open ? <X className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
        {open ? "Close" : "Chat with Ani"}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-20 right-3 left-3 sm:left-auto sm:right-5 z-[70] flex h-[min(620px,calc(100vh-120px))] sm:w-[400px] flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0d1013] shadow-2xl"
            data-testid="wingman-panel"
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <span className="grid h-10 w-10 place-items-center rounded-full border border-emerald-400/50 font-display text-lg text-emerald-300">A</span>
              <div>
                <p className="font-display text-xl leading-none">Ani</p>
                <p className="mt-1 text-xs text-zinc-400">PLUK HR recruitment assistant</p>
              </div>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-5" data-lenis-prevent data-testid="wingman-messages">
              {msgs.map((m, i) => <Bubble key={i} m={m} />)}
              {busy && <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />}
              <div ref={endRef} />
            </div>
            {options.length > 0 && (
              <div className="flex max-h-28 flex-wrap gap-2 overflow-y-auto border-t border-white/10 px-4 py-3" data-lenis-prevent data-testid="wingman-options">
                {options.map((o, i) => (
                  <button key={o} onClick={() => send(o)} disabled={busy} className="rounded-full border border-emerald-400/40 px-3 py-1 text-xs text-emerald-200 hover:bg-emerald-500 hover:text-[#0a0c0e] transition-colors" data-testid={`wingman-option-${i}`}>
                    {o}
                  </button>
                ))}
              </div>
            )}
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-white/10 px-3 py-3">
              <button type="button" onClick={() => fileRef.current?.click()} className="rounded-full p-2.5 text-zinc-300 hover:text-emerald-300" aria-label="Attach CV" data-testid="wingman-attach-btn">
                <Paperclip className="h-4 w-4" />
              </button>
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="wingman-file-input" />
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your reply" className="flex-1 bg-transparent px-2 py-2 text-sm text-zinc-100 outline-none placeholder:text-zinc-500" data-testid="wingman-input" />
              <button type="submit" disabled={busy || !text.trim()} className="grid h-10 w-10 place-items-center rounded-full bg-emerald-500 text-[#0a0c0e] disabled:opacity-40" aria-label="Send" data-testid="wingman-send-btn">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
